import { bindable, observable, autoinject, computedFrom } from 'aurelia-framework';
import { ComparisonType, IReportTemplateColumn } from '../models/report-template';
import { EventAggregator } from 'aurelia-event-aggregator';
import { IScopeAM } from '../../scopes/scope-service';
import { Municipality, MunicipalityService } from '../../municipalities/municipality-service';
import { flatten } from 'lodash';
import {
	OrganizationalUnitService,
	OrganizationalNodeModel
} from '../../customers/organizational-units/organizational-unit-service';
import { IReportHeaderCellAM } from '../report-api';

@autoinject
export class ReportColumnAdd {
	@bindable index: number;
	@bindable direction = 'right';
	@bindable scopes: IScopeAM[];
	@bindable columns: IReportHeaderCellAM[];
	@bindable comparisonTypes: ComparisonType[];
	@bindable allowOrganizationUnits: boolean;
	@bindable addColumn: (params: { index: number, column: IReportTemplateColumn }) => void;
	@bindable disableMunicipalitySelection: boolean;

	@observable showAddColumn: boolean;

	municipalities: Municipality[];
	organizationalUnits: OrganizationalVM[];
	buttonColor = 'default';
	loading = false;

	constructor(
		private eventAggregator: EventAggregator,
		private municipalityService: MunicipalityService,
		private organizationalUnitService: OrganizationalUnitService) {
	}

	attached() {
		this.eventAggregator.subscribe(
			'hide-report-column-add-forms',
			(index: number) => {
				if (index !== this.index) { this.showAddColumn = false; }
			});
	}

	showAddColumnChanged(newValue) {
		if (newValue) {
			this.eventAggregator.publish('hide-report-column-add-forms', this.index);
		}

		this.buttonColor = newValue ? 'primary' : 'default';
	}

	onAddColumn(column: IReportTemplateColumn) {
		this.addColumn({ index: this.index, column });
	}

	async changeScope(scope: IScopeAM) {
		if (scope == null) {
			this.municipalities = [];
			this.organizationalUnits = [];
			return;
		}

		this.loading = true;
		const [municipalities, units] = await Promise.all([
			this.disableMunicipalitySelection
				? Promise.resolve([])
				: this.municipalityService.getMunicipalitiesForFiscalYear(scope.fiscalYearId),
			this.allowOrganizationUnits
				? this.organizationalUnitService.getOrganizationalUnitsForFiscalYear(scope.fiscalYearId, false)
				: Promise.resolve([])
		]);
		this.municipalities = municipalities;
		this.organizationalUnits = this.flattenUnits(units).map(unit => new OrganizationalVM(unit));
		this.loading = false;
	}

	private flattenUnits(units: OrganizationalNodeModel[]) {
		const reducer = (flatArray: OrganizationalNodeModel[], node) => {
			const getNodes = (n) => {
				const childNodes = flatten(n.children.map(getNodes));
				return [n, ...childNodes];
			};
			return [...flatArray, ...getNodes(node)];
		};

		const flatted = units.reduce(reducer, []);
		return flatted;
	}

	indexOfOrganizationalUnit(searchString: string) {
		if (!searchString) { return null; }

		const ignoreCaseIndexOf = (text: string) => text.toLowerCase().indexOf(searchString.toLowerCase());

		const compare = (a, b) => a.matchIndex === 0 || b.matchIndex === 0 ? a.matchIndex - b.matchIndex : 0;

		const results = this.organizationalUnits
			.map(vm => `${vm.unit.identifier} ${vm.unit.name}`)
			.map((text, index) => ({ originalIndex: index, matchIndex: ignoreCaseIndexOf(text) }))
			.filter(result => result.matchIndex !== -1);

		const startsWith = results.filter(result => result.matchIndex === 0);

		if (startsWith.length > 0) {
			return startsWith[0].originalIndex;
		}

		return results.length > 0 ? results[0].originalIndex : null;
	}
}

export class OrganizationalVM {
	private readonly levelMargin = 10;

	unit: OrganizationalNodeModel;
	margin: number;
	textLimit: number;
	nodeTypeClass: string;

	constructor(unit: OrganizationalNodeModel) {
		this.unit = unit;
		this.margin = this.levelMargin * (unit.level - 1);
		// Make limit of characters in title text proportional to the left margin of the node.
		this.textLimit = 52 - ((unit.level - 1) * (this.levelMargin / 5));
	}
}
